/**
 * Dependency blockers report.
 * Route: /reports/dependency-blockers
 * Renders the report. Blocker math lives in logic/deriveDependencyBlockers.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  HoverCard,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { fetchAllLinks, fetchAllTasks } from '../../api/schedulingApis';
import { usePortalProject } from '../../contexts/PortalProjectContext';
import {
  deleteDashboardInFlight,
  deleteDashboardMemoryCache,
  getDashboardInFlight,
  getDashboardMemoryCache,
  setDashboardInFlight,
  setDashboardMemoryCache,
  type DashboardData,
} from '../../dashboard/dashboardProjectCache';
import { loadTasksFromDB } from '../../stores/db';
import { pendingWithDisplayName } from '../../utils/linkedIssueHelpers';
import { openPortalUrl } from '../../utils/portalNavigation';
import { parseDateSafe } from '../../utils/utils';
import { prepareDashboardData } from '../dashboard/logic/prepareDashboardData';
import {
  deriveDependencyBlockers,
  type DependencyBlockerRow,
} from './logic/deriveDependencyBlockers';

const CARD_RADIUS = 'sm';
const ISSUES_TICKET_PATH = 'requests/dprIssueTrackerProcess';

function formatDate(value: string | null | undefined): string {
  const d = parseDateSafe(value ?? null);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return `${Math.round(Number(value))}%`;
}

function KpiInfoCard({
  title,
  value,
  subtitle,
  info,
}: {
  title: string;
  value: React.ReactNode;
  subtitle: string;
  info: string;
}) {
  return (
    <Card withBorder radius={CARD_RADIUS} p="md" style={{ position: 'relative' }}>
      <HoverCard width={280} shadow="md" withArrow position="bottom-end" openDelay={150} closeDelay={80}>
        <HoverCard.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={`About ${title}`}
            style={{ position: 'absolute', top: 8, right: 8 }}
          >
            <IconInfoCircle size={16} stroke={1.5} />
          </ActionIcon>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <Text size="sm">{info}</Text>
        </HoverCard.Dropdown>
      </HoverCard>
      <Text size="xs" c="dimmed" pr={28}>
        {title}
      </Text>
      <Title order={3}>{value}</Title>
      <Text size="xs" c="dimmed">
        {subtitle}
      </Text>
    </Card>
  );
}

function DependencyBlockersPage() {
  const { projectId, projectName } = usePortalProject();
  const projectLabel = projectName || projectId || 'your project';

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardData>({ tasks: [], links: [] });
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const hasDataRef = React.useRef(false);

  useEffect(() => {
    hasDataRef.current = data.tasks.length > 0 || data.links.length > 0;
  }, [data.tasks.length, data.links.length]);

  const fetchFromApiDeduped = useCallback(async (): Promise<DashboardData> => {
    const existing = getDashboardInFlight(projectId);
    if (existing) return existing;
    const requestPromise = (async () => {
      const [tasksResponse, linksResponse] = await Promise.all([
        fetchAllTasks({ projectId }),
        fetchAllLinks({ projectId }).catch(() => ({ data: [] })),
      ]);
      const tasks = Array.isArray(tasksResponse?.data) ? tasksResponse.data : [];
      const links = Array.isArray(linksResponse?.data) ? linksResponse.data : [];
      const result = prepareDashboardData(tasks, links);
      setDashboardMemoryCache(projectId, result);
      return result;
    })();
    setDashboardInFlight(projectId, requestPromise);
    try {
      return await requestPromise;
    } finally {
      deleteDashboardInFlight(projectId);
    }
  }, [projectId]);

  const load = useCallback(
    async (opts?: { forceRefresh?: boolean }) => {
      const forceRefresh = !!opts?.forceRefresh;
      if (!projectId) {
        setData({ tasks: [], links: [] });
        setLoading(false);
        return;
      }

      if (!hasDataRef.current) setLoading(true);
      else if (forceRefresh) setRefreshing(true);
      setError(null);

      try {
        if (forceRefresh) {
          deleteDashboardMemoryCache(projectId);
        }

        if (!forceRefresh) {
          const inMemory = getDashboardMemoryCache(projectId);
          if (inMemory && inMemory.tasks.length > 0) {
            setData(prepareDashboardData(inMemory.tasks, inMemory.links));
            return;
          }
          const fromDb = await loadTasksFromDB(projectId);
          if (fromDb?.tasks?.length) {
            const dbData = prepareDashboardData(fromDb.tasks, fromDb.links ?? []);
            setDashboardMemoryCache(projectId, dbData);
            setData(dbData);
            return;
          }
        }

        const apiData = await fetchFromApiDeduped();
        setData(apiData);
      } catch {
        setError('Could not load dependency blockers right now.');
        setData((prev) => (prev.tasks.length > 0 ? prev : { tasks: [], links: [] }));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [projectId, fetchFromApiDeduped],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const report = useMemo(
    () => deriveDependencyBlockers(data.tasks, data.links),
    [data.tasks, data.links],
  );

  useEffect(() => {
    if (selectedRowId != null && !report.rows.some((r) => r.id === selectedRowId)) {
      setSelectedRowId(null);
    }
  }, [report.rows, selectedRowId]);

  const selectedRow: DependencyBlockerRow | null = useMemo(
    () => report.rows.find((r) => r.id === selectedRowId) ?? null,
    [report.rows, selectedRowId],
  );

  const toggleRow = (rowId: string) => {
    setSelectedRowId((prev) => (prev === rowId ? null : rowId));
  };

  const showSkeleton = loading && data.tasks.length === 0;

  return (
    <Box style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
      <Box py="xl" px="md" style={{ width: '100%' }}>
        <Stack gap="xl">
          <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
            <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
              <Title order={2}>Dependency blockers</Title>
              <Text c="dimmed" size="sm">
                Successor tasks blocked by open Critical issues on their direct predecessors for{' '}
                {projectLabel}.
              </Text>
            </Stack>
            <Button
              variant="default"
              onClick={() => void load({ forceRefresh: true })}
              loading={loading || refreshing}
              styles={{
                root: {
                  backgroundColor: 'var(--mantine-color-body)',
                  color: 'var(--mantine-color-text)',
                  border: '1px solid var(--mantine-color-default-border)',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: 'var(--mantine-color-default-hover)',
                  },
                },
              }}
            >
              Refresh
            </Button>
          </Group>

          {error ? (
            <Paper radius={CARD_RADIUS} p="md" withBorder>
              <Text c="red">{error}</Text>
            </Paper>
          ) : null}

          {showSkeleton ? (
            <>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <Card key={`kpi-sk-${idx}`} withBorder radius={CARD_RADIUS} p="md">
                    <Skeleton height={10} width="50%" mb="md" />
                    <Skeleton height={28} width="40%" />
                  </Card>
                ))}
              </SimpleGrid>
              <Card withBorder radius={CARD_RADIUS} p="md">
                <Skeleton height={200} />
              </Card>
            </>
          ) : (
            <>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                <KpiInfoCard
                  title="Blocked successors"
                  value={report.kpis.blockedSuccessorCount}
                  subtitle="unique downstream tasks"
                  info="Count of unique successor tasks that have at least one direct predecessor with an open Critical issue."
                />
                <KpiInfoCard
                  title="Blocking predecessors"
                  value={report.kpis.blockingPredecessorCount}
                  subtitle="with Critical issues + outgoing links"
                  info="Count of unique predecessor tasks that have open Critical issues and at least one outgoing dependency to a successor."
                />
                <KpiInfoCard
                  title="Open Critical issues"
                  value={report.kpis.openCriticalIssueCount}
                  subtitle="on blocking predecessors"
                  info="Unique open Critical tickets linked to predecessors that block at least one successor via a direct dependency."
                />
                <KpiInfoCard
                  title="Causing delay"
                  value={report.kpis.currentlyCausingDelayCount}
                  subtitle="currently affecting tickets"
                  info="Unique open Critical tickets among blockers that are marked as currently affecting the schedule."
                />
              </SimpleGrid>

              <Box
                className="dependency-blockers-split"
                style={{
                  display: 'grid',
                  gridTemplateColumns: selectedRow ? 'minmax(0, 1.6fr) minmax(280px, 1fr)' : '1fr',
                  gap: 16,
                  alignItems: 'stretch',
                  minHeight: 420,
                }}
              >
                <Card
                  withBorder
                  radius={CARD_RADIUS}
                  p="md"
                  style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}
                >
                  <Group justify="space-between" mb="sm" style={{ flexShrink: 0 }}>
                    <Title order={4}>Blocked successors</Title>
                    <Badge variant="light">{report.rows.length}</Badge>
                  </Group>
                  {report.rows.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      No successor tasks are blocked by open Critical issues on their predecessors.
                    </Text>
                  ) : (
                    <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                      <Table.ScrollContainer minWidth={selectedRow ? 560 : 720}>
                        <Table highlightOnHover verticalSpacing="sm">
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Successor</Table.Th>
                              <Table.Th>Dependency</Table.Th>
                              <Table.Th>Blocking predecessor</Table.Th>
                              <Table.Th ta="right">Issues</Table.Th>
                              <Table.Th>Delay</Table.Th>
                              <Table.Th ta="right">Succ. %</Table.Th>
                              <Table.Th>Succ. finish</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {report.rows.map((row) => {
                              const active = selectedRowId === row.id;
                              return (
                                <Table.Tr
                                  key={row.id}
                                  style={{
                                    cursor: 'pointer',
                                    background: active ? 'var(--mantine-color-default-hover)' : undefined,
                                  }}
                                  onClick={() => toggleRow(row.id)}
                                >
                                  <Table.Td>
                                    <Text size="sm" fw={500}>
                                      {row.successorName}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text size="sm" c="dimmed">
                                      {row.dependencyLabel}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text size="sm">{row.predecessorName}</Text>
                                  </Table.Td>
                                  <Table.Td ta="right">
                                    <Text size="sm">{row.issues.length}</Text>
                                  </Table.Td>
                                  <Table.Td>
                                    {row.causingDelay ? (
                                      <Badge size="xs" color="red" variant="light">
                                        Causing delay
                                      </Badge>
                                    ) : (
                                      <Text size="sm" c="dimmed">
                                        —
                                      </Text>
                                    )}
                                  </Table.Td>
                                  <Table.Td ta="right">
                                    <Text size="sm">{formatPercent(row.successorPercentComplete)}</Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text size="sm" c="dimmed">
                                      {formatDate(row.successorFinish)}
                                    </Text>
                                  </Table.Td>
                                </Table.Tr>
                              );
                            })}
                          </Table.Tbody>
                        </Table>
                      </Table.ScrollContainer>
                    </Box>
                  )}
                </Card>

                {selectedRow ? (
                  <Card
                    className="dependency-blockers-detail"
                    withBorder
                    radius={CARD_RADIUS}
                    p="md"
                    style={{
                      position: 'sticky',
                      top: 16,
                      alignSelf: 'start',
                      maxHeight: 'calc(100vh - 120px)',
                      overflow: 'auto',
                      minWidth: 0,
                    }}
                  >
                    <Group justify="space-between" mb="sm" wrap="nowrap">
                      <Title order={4}>Blocker detail</Title>
                      <Button variant="subtle" size="xs" onClick={() => setSelectedRowId(null)}>
                        Clear
                      </Button>
                    </Group>
                    <Stack gap="md">
                      <Stack gap={2}>
                        <Text size="xs" c="dimmed">
                          Successor
                        </Text>
                        <Text size="sm" fw={500}>
                          {selectedRow.successorName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatPercent(selectedRow.successorPercentComplete)} · finish{' '}
                          {formatDate(selectedRow.successorFinish)}
                        </Text>
                      </Stack>
                      <Stack gap={2}>
                        <Text size="xs" c="dimmed">
                          Blocking predecessor
                        </Text>
                        <Text size="sm" fw={500}>
                          {selectedRow.predecessorName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          Dependency {selectedRow.dependencyLabel}
                        </Text>
                      </Stack>
                      <Stack gap="xs">
                        <Text size="xs" c="dimmed">
                          Open Critical issues ({selectedRow.issues.length})
                        </Text>
                        {selectedRow.issues.map((issue) => (
                          <UnstyledButton
                            key={issue.ticketId}
                            onClick={() =>
                              openPortalUrl(ISSUES_TICKET_PATH, {
                                query: { ticketId: issue.ticketId },
                              })
                            }
                            style={{
                              display: 'block',
                              width: '100%',
                              borderRadius: 8,
                              padding: '8px 10px',
                              border: '1px solid var(--mantine-color-default-border)',
                              background: 'var(--mantine-color-default)',
                              textAlign: 'left',
                            }}
                          >
                            <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
                              <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                                <Text size="sm" fw={500} truncate>
                                  {issue.title || issue.ticketId}
                                </Text>
                                <Text size="xs" c="dimmed" truncate>
                                  {issue.category.trim() || 'Uncategorized'}
                                  {' · '}
                                  Pending with {pendingWithDisplayName(issue.pendingWith)}
                                </Text>
                              </Stack>
                              {issue.currentlyAffected ? (
                                <Badge size="xs" color="red" variant="light" style={{ flexShrink: 0 }}>
                                  Causing delay
                                </Badge>
                              ) : null}
                            </Group>
                          </UnstyledButton>
                        ))}
                      </Stack>
                    </Stack>
                  </Card>
                ) : null}
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export default DependencyBlockersPage;
